import { RouteRecordRaw, RouteComponent, RouteLocationNormalized } from 'vue-router';
import { IRoute } from './IRoute';
import { EmbeddedState } from '@/url/EmbeddedState';
import { IEmbeddedStateMutable } from '@/url/IEmbeddedStateMutable';
import { IEmbeddedState } from '@/url/IEmbeddedState';
import { NavigationTargetResult } from './NavigationTargetResult';

export abstract class RouteFragment implements IRoute {
	private _parent: IRoute | null = null;
	private _children: RouteFragment[] = [];

	public readonly name: string;
	public static readonly fragmentType: typeof RouteFragment;

	public constructor(name: string) {
		this.name = name;
	}

	public get fullyQualifiedName(): string {
		return this.parent === null ? this.name : `${this.parent.fullyQualifiedName}.${this.name}`;
	}

	public get parent(): IRoute | null {
		return this._parent;
	}

	public get children(): readonly IRoute[] {
		return this._children;
	}

	public toRouteDefinition(): RouteRecordRaw {
		return {
			path: this.selfRoutePath(),
			component: () => this.selfRouteComponent(),
			name: this.fullyQualifiedName,
			children: this.children.map((c) => c.toRouteDefinition()),
		};
	}

	public setParent(parent: RouteFragment): void {
		this._parent = parent;
	}

	public addChild(child: RouteFragment): void {
		child.setParent(this);
		this._children.push(child);
	}

	public includeFallbacks(): void {
		for (const prototype of this.selfProvidePrototypeOfFallbacks()) {
			this.addChild(prototype.instantiate());
		}
	}

	public async retrieveEmbeddedStateFrom(destination: RouteLocationNormalized): Promise<IEmbeddedState> {
		const embeddedState = new EmbeddedState();

		await this.decodeByPopulatingFrom(embeddedState, destination);
		return embeddedState;
	}

	public async toNavigationTarget(embeddedState: IEmbeddedState): Promise<NavigationTargetResult | null> {
		const childrenToNavigationTarget = await this.childrenToNavigationTarget(embeddedState);
		const navigationTarget = childrenToNavigationTarget ?? this.selfSelectNavigationTarget(embeddedState);

		if (navigationTarget !== null) {
			return await this.selfEncodeToNavigationTarget(navigationTarget);
		}

		return null;
	}

	public async notSupports(embeddedState: IEmbeddedState): Promise<boolean> {
		return !(await this.supports(embeddedState));
	}

	public async supports(embeddedState: IEmbeddedState): Promise<boolean> {
		return await this.selfSupports(embeddedState);
	}

	protected selfEncodeToNavigationTarget(
		navigationTargetResult: NavigationTargetResult
	): Promise<NavigationTargetResult> {
		return Promise.resolve(navigationTargetResult);
	}

	protected selfSelectNavigationTarget(embeddedState: IEmbeddedState): NavigationTargetResult {
		return new NavigationTargetResult({ name: this.fullyQualifiedName }, embeddedState.toMutable());
	}

	protected selfProvidePrototypeOfFallbacks(): RouteFragment[] {
		return [];
	}

	
	protected async selfDecodeByPopulatingFrom(
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		_embeddedState: IEmbeddedStateMutable,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		_destination: RouteLocationNormalized
	): Promise<void> {
		// Extension point for derived instances
	}

	private async decodeByPopulatingFrom(
		embeddedState: IEmbeddedStateMutable,
		destination: RouteLocationNormalized
	): Promise<void> {
		await this.selfDecodeByPopulatingFrom(embeddedState, destination);
		await this.childrenDecodeByPopulatingFrom(embeddedState, destination);
	}

	private async childrenDecodeByPopulatingFrom(
		embeddedState: IEmbeddedStateMutable,
		destination: RouteLocationNormalized
	): Promise<void> {
		for (const child of this._children.filter((c) => c.isPartOf(destination))) {
			await child.decodeByPopulatingFrom(embeddedState, destination);
		}
	}

	private async childrenToNavigationTarget(embeddedState: IEmbeddedState): Promise<NavigationTargetResult | null> {
		for (const child of this.children) {
			if (await child.notSupports(embeddedState)) {
				continue;
			}

			const navigationTarget = await child.toNavigationTarget(embeddedState);
			if (navigationTarget !== null) {
				return navigationTarget;
			}
		}

		return null;
	}

	private isPartOf(destination: RouteLocationNormalized): boolean {
		return destination.matched.some((record) => record.name === this.fullyQualifiedName);
	}

	public abstract instantiate(): RouteFragment;

	protected abstract selfRoutePath(): string;

	protected abstract selfRouteComponent(): Promise<RouteComponent>;

	protected abstract selfSupports(embeddedState: IEmbeddedState): Promise<boolean>;
}
